import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ConfirmBoxComponent } from 'app/shared/components/confirm-box/confirm-box.component';
import { AuthService } from 'app/core/auth/auth.service';
import { CustomerService } from 'app/core/services/customer.service';
import { PackageService } from 'app/core/services/package.service';
import { SendgridService } from 'app/core/services/sendgrid.service';
import { HistoryService } from 'app/core/services/history.service';
import { equifaxConfig } from 'app/configs';
import { minimumTotalAmount } from 'app/configs/minimum.total.amount';
import { getAmountWithTax } from 'app/shared/utils/tax';
import { getCreditScoreType } from 'app/shared/utils';
import { currentLoanProvider, isSHPFinanceProvider } from 'app/helpers/finance';
import { v4 as uuidv4 } from 'uuid';

export interface SendRemoteQuoteEmailOptions {
  customerGuid: string;
  odooCustomerId: number;
  currentCustomer: any;
  currentPermission?: string;
  accountId?: string;
  envelopeId?: string;
  creditProducts?: any[];
  isSHPFinance?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class RemoteQuoteService {
  private static readonly RESEND_GUARD_MS = 2 * 60 * 1000;

  constructor(
    private _router: Router,
    private _authService: AuthService,
    private _customerService: CustomerService,
    private _packageService: PackageService,
    private _sendGridService: SendgridService,
    private _historyService: HistoryService,
    private _matDialog: MatDialog,
  ) {}

  /**
   * Sends remote quote/payment email to customer, updates customer metadata,
   * creates internal note, sends SendGrid email, logs history,
   * shows confirmation dialog to salesperson, and routes to appropriate dashboard.
   */
  async sendRemoteQuoteEmail(options: SendRemoteQuoteEmailOptions): Promise<void> {
    const {
      customerGuid,
      odooCustomerId,
      currentCustomer,
      accountId,
      envelopeId,
      creditProducts = [],
      isSHPFinance = isSHPFinanceProvider,
    } = options;

    let currentPermission = options.currentPermission;
    if (!currentPermission) {
      try {
        currentPermission = await this._authService.getCurrentPermission();
      } catch (err) {
        console.error('Failed to get current permission:', err);
        currentPermission = 'admin';
      }
    }

    // Skip a repeat send within 2 minutes in this tab. After the rep signs, the sign page poller
    // and sign-return (which replaces the page) can both get here; a quick reload can too.
    // A later dashboard resume sends again on purpose.
    const sentKey = `remoteQuoteSent:${customerGuid}`;
    if (this._wasSent(sentKey)) {
      return this._showSentDialog(currentPermission);
    }

    const email = currentCustomer?.email;
    const firstName = (currentCustomer?.firstName || '').trim();
    const firstNameCapitalized = firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1) : '';

    const lastName = (currentCustomer?.lastName || '').trim();
    const lastNameCapitalized = lastName ? lastName.charAt(0).toUpperCase() + lastName.slice(1) : '';

    const fullName = `${firstNameCapitalized} ${lastNameCapitalized}`.trim();

    let remotePaymentType = currentCustomer?.remotePaymentType;
    if (!remotePaymentType) {
      if (
        currentCustomer?.isLoanApproved ||
        (currentCustomer?.financeAmount && currentCustomer.financeAmount > 0)
      ) {
        remotePaymentType = 'finance';
      } else {
        remotePaymentType = 'cash';
      }
    }

    const isFinance = remotePaymentType === 'finance';
    const baseUrl = window.location.origin;
    let remoteUrl = `${baseUrl}/auth/remote/payment/cash/${customerGuid}/${odooCustomerId}`;

    if (
      (currentCustomer?.financeAmount > 0 && currentCustomer?.isVirtualAppointment) ||
      (isFinance && !currentCustomer?.isVirtualAppointment)
    ) {
      remoteUrl = `${baseUrl}/auth/remote/payment/finance/${customerGuid}/${odooCustomerId}`;
    }

    /* Update customer payload */
    const isLendproAttempted = isFinance
      ? false
      : Boolean(currentCustomer?.isLendproAttempted);

    const updateCustomerPayload: any = {
      isLendproAttempted,
      isReset: false,
    };
    if (accountId) {
      updateCustomerPayload.accountId = accountId;
    }
    if (envelopeId) {
      updateCustomerPayload.envelopeId = envelopeId;
    }

    await this._customerService.updateCustomer(customerGuid, updateCustomerPayload);

    const paymentType = remotePaymentType.charAt(0).toUpperCase() + remotePaymentType.slice(1);
    const message = `${paymentType} Url : ${remoteUrl}`;
    await this._customerService.addNewInternalNote(customerGuid, message);

    /* Parse products and addOns */
    let products: any[] = [];
    try {
      products = typeof currentCustomer?.products === 'string'
        ? JSON.parse(currentCustomer.products || '[]')
        : (currentCustomer?.products || []);
    } catch (e) {
      console.error('Error parsing customer products:', e);
      products = [];
    }

    let addOnRMRs: any[] = [];
    try {
      addOnRMRs = typeof currentCustomer?.addOns === 'string'
        ? JSON.parse(currentCustomer.addOns || '[]')
        : (currentCustomer?.addOns || []);
    } catch (e) {
      console.error('Error parsing customer addOns:', e);
      addOnRMRs = [];
    }

    let currentPlan: any = null;
    if (currentCustomer?.package) {
      try {
        currentPlan = await this._packageService.retrievePackage(currentCustomer.package);
      } catch (e) {
        console.error('Error retrieving package:', e);
      }
    }

    const integrationFee = currentCustomer?.integrationFee || 0;
    const creditFee = currentCustomer?.creditFee || 0;

    let productsOnlyTotal = 0;
    if (Array.isArray(products)) {
      products.forEach((obj: any) => {
        const prices = Array.isArray(obj.prices) ? obj.prices : [];
        const sum = prices.reduce((acc: number, cur: number) => acc + (Number(cur) || 0), 0);
        obj.priceTotal = this._addZeroes(sum);
        productsOnlyTotal += sum;
        obj.quantity = prices.length;
      });
    }

    let mailAddonTotal = 0;
    if (Array.isArray(addOnRMRs)) {
      addOnRMRs.forEach((obj: any) => {
        if (obj.selected) {
          const price = Number(obj.price) || 0;
          mailAddonTotal += price;
          obj.price = this._addZeroes(price);
        }
      });
    }

    /* Credit score & SHP finance params */
    const creditScore = currentCustomer?.creditScore;
    const creditScoreType = getCreditScoreType(creditScore, currentCustomer?.newCreditPolicy);
    let hasFinanceRestrictions = false;
    const isSHPProvider = isSHPFinanceProvider;
    let financedAmount = 0;
    let shpTotal: any = isFinance
      ? this._addZeroes((currentCustomer?.mailTotal || 0) / 60)
      : 0;

    const shpFlag = Boolean(
      currentCustomer?.loanType &&
      currentCustomer.loanType === currentLoanProvider &&
      isFinance
    );

    const creditPolicy = equifaxConfig.credit(currentCustomer?.newCreditPolicy);
    if (currentCustomer?.newCreditPolicy && creditScore < creditPolicy.A && creditScore >= creditPolicy.MIN) {
      hasFinanceRestrictions = shpFlag;
      financedAmount = getAmountWithTax(productsOnlyTotal);
      shpTotal = (financedAmount / 60) > 0 ? this._addZeroes(financedAmount / 60) : this._addZeroes(0);
      if (isSHPFinance && creditProducts.length > 0 && currentCustomer?.creditProduct) {
        const currentCreditProduct = creditProducts.find(
          (product: any) => product.shp_product_id === currentCustomer.creditProduct
        );
        if (currentCreditProduct && currentCreditProduct.duration) {
          shpTotal = (financedAmount / currentCreditProduct.duration) > 0
            ? this._addZeroes(financedAmount / currentCreditProduct.duration)
            : this._addZeroes(0);
        }
      }
    }

    let planStr: any = null;
    if (currentCustomer?.lendproResponse) {
      try {
        planStr = typeof currentCustomer.lendproResponse === 'string'
          ? JSON.parse(currentCustomer.lendproResponse)
          : currentCustomer.lendproResponse;
      } catch (e) {
        console.error('Error parsing lendproResponse:', e);
      }
    }
    const monthlyPayment = isFinance ? this._addZeroes(planStr?.monthlyPayment) : 0;

    const data = {
      email,
      remoteUrl,
      fullName,
      lastNameCapitalized,
      firstNameCapitalized,
      remotePaymentType: currentCustomer?.remotePaymentType,
      currentPlan,
      integrationFee: this._addZeroes(integrationFee),
      products,
      addOnRMRs: addOnRMRs || [],
      creditFee: this._addZeroes(creditFee),
      planName: currentPlan?.name || '',
      monthlyPayment: this._addZeroes((Number(currentCustomer?.mailMonthlyPayment) || 0) + mailAddonTotal),
      totalTax: this._addZeroes(currentCustomer?.totalTax),
      total: this._addZeroes(currentCustomer?.mailTotal),
      shpTotal: monthlyPayment,
      shpFlag,
      convenienceFlag: Boolean(currentCustomer?.isConvenienceFeeAdded),
      hasFinanceRestrictions,
      isSHPProvider,
      financedAmount: this._addZeroes(currentCustomer?.financeAmount),
    };

    await this._sendGridService.sendQuoteMail(data);
    // Mark only after the send, so an interrupted page never skips the email
    this._markSent(sentKey);

    // Log now, not when the dialog closes: sign-return may replace this page first
    try {
      const currentUser = this._authService.currentAuthValue;
      await this._historyService.addNewHistory({
        guid: uuidv4(),
        userName: currentUser?.display_name,
        userEmail: currentUser?.email,
        customerName: `${currentCustomer?.firstName || ''} ${currentCustomer?.lastName || ''}`.trim(),
        customerEmail: currentCustomer?.email,
        action: 'Sent an email to customer for signing document',
        actionDetail: JSON.stringify(currentCustomer),
        display_name: currentCustomer?.display_name,
        odooId: odooCustomerId,
      });
    } catch (histErr) {
      console.error('Error adding history log:', histErr);
    }

    return this._showSentDialog(currentPermission);
  }

  private _showSentDialog(currentPermission?: string): Promise<void> {
    return new Promise<void>((resolve) => {
      const confirmDialogRef = this._matDialog.open(ConfirmBoxComponent, {
        disableClose: false,
        panelClass: 'confirm-box-dialog',
        data: {
          content: 'An email has been sent to your customer requesting that they sign the agreement.',
          title: 'Customer Sign',
        },
      });

      confirmDialogRef.afterClosed().subscribe(() => {
        try {
          this._navigateToDashboard(currentPermission);
        } catch (navErr) {
          console.error('Error navigating to dashboard:', navErr);
        }

        resolve();
      });
    });
  }

  private _wasSent(key: string): boolean {
    try {
      const sentAt = Number(sessionStorage.getItem(key));
      return sentAt > 0 && Date.now() - sentAt < RemoteQuoteService.RESEND_GUARD_MS;
    } catch {
      return false;
    }
  }

  private _markSent(key: string): void {
    try {
      sessionStorage.setItem(key, String(Date.now()));
    } catch {
      // Storage blocked: fall back to sending again, as before
    }
  }

  private _navigateToDashboard(permission?: string): void {
    if (permission === 'admin') {
      this._router.navigateByUrl('/admin/dashboard');
    } else if (permission === 'manager') {
      this._router.navigateByUrl('/manager/dashboard');
    } else if (permission === 'user') {
      this._router.navigateByUrl('/user/dashboard');
    } else {
      this._router.navigateByUrl('/admin/dashboard');
    }
  }

  private _addZeroes(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '0.00';
    }
    const num = Number(value);
    if (isNaN(num)) {
      return '0.00';
    }
    return num.toFixed(2);
  }
}
